import { useEffect, useState } from 'react';
import { useStore } from '../../../../utils/store';
import { useNavigate, useParams } from 'react-router-dom';
import HttpService from '../../../../services/HttpService';
import Constants from '../../../../utils/constants';
import FormFieldData from '../../../../Models/FormFieldData';
import { Stack } from '@mui/material';
import CommonForm from '../../../../components/form/CommonForm';
import AlertMessageComponent from '../../../../components/AlertMessageComponent';
import { useAlert } from '../../../../components/alertContext';

const AdhocFormCommon: React.FC = () => {
  interface adhocFormCommon {
    termText: { required: boolean; value: string };
    id: { required: boolean; value: any };
  }

  const { getAdmin } = useStore();
  const { currentId } = useParams<{ currentId: any }>();
  const navigate = useNavigate();

  const { alert, setAlert, handleCloseAlert } = useAlert();
  const [loading, setLoading] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<any>(null);
  const [termsForm, setTermsForm] = useState<adhocFormCommon>({
    termText: { required: true, value: '' },
    id: { required: false, value: null },
  });

  useEffect(() => {
    if (currentId) {
      if (currentId !== 'Add') {
        fetchSingleRecord(currentId);
      }
    } else {
      setCurrentRecord(null);
    }
  }, [currentId]);

  const fetchSingleRecord = async (id: string) => {
    try {
      const response = await HttpService.get(
        `${Constants.apiURL + Constants.adhocTermsURL}/${id}`,
        { headers: { Authorization: `Bearer ${getAdmin()}` } },
        null
      );
      if (response.success) {
        handleRowClick(response.data);
      }
    } catch (error: any) {
      console.error(`Error fetching data:`, error);
      setAlert({
        type: 'error',
        message: error.message,
        open: true,
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setTermsForm((prevData) => ({
      ...prevData,
      [name]: { required: true, value: value },
    }));
  };

  const handleRowClick = (record: any) => {
    setCurrentRecord(record);
    setTermsForm({
      termText: { required: true, value: record.termText },
      id: { required: false, value: record.id },
    });
  };

  const handleSave = async () => {
    setLoading(true);
    const reqData: any = { termText: termsForm.termText.value };
    const endpoint = `${Constants.apiURL + Constants.adhocTermsURL}`;
    let response: any;
    try {
      if (currentRecord) {
        reqData['id'] = currentRecord.id;
        response = await HttpService.put(endpoint, reqData, {
          headers: { Authorization: `Bearer ${getAdmin()}` },
        });
      } else {
        response = await HttpService.post(endpoint, reqData, {
          headers: { Authorization: `Bearer ${getAdmin()}` },
        });
      }

      if (response.success) {
        clearForm();
      }
    } catch (error: any) {
      console.error(`Error fetching data:`, error);
      setAlert({
        type: 'error',
        message: error.message,
        open: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const clearForm = () => {
    setTermsForm({
      termText: { required: true, value: '' },
      id: { required: false, value: null },
    });
    setAlert({
      type: 'error',
      message: '',
      open: false,
    });
    setCurrentRecord(null);
    navigate('/data-management/adhoc-terms-conditions');
  };

  const formFields: FormFieldData[] = [
    {
      onChange: handleInputChange,
      validationRules: [(value) => (!value ? 'Text is required' : '')],
      label: 'Text',
      isRequired: termsForm.termText.required,
      value: termsForm.termText.value,
      keyname: 'termTextForm',
      name: 'termText',
      minRows: 7,
      size: 12,
      type: 'textareaField',
    },
  ];

  return (
    <Stack>
      <CommonForm
        fields={formFields}
        onCancel={() => {}}
        formReset={clearForm}
        formTitle={currentId !== 'Add' ? 'Update Term' : 'Add New Term'}
        isLoading={loading}
        isUpdate={currentId !== 'Add' ? true : false}
        onSave={handleSave}
        actionButtonPosition="bottom"
      />
      <AlertMessageComponent
        type={alert.type}
        open={alert.open}
        handleClose={handleCloseAlert}
        message={alert.message}
      />
    </Stack>
  );
};

export default AdhocFormCommon;
